import { motion } from "framer-motion";
import { Clock, Eye } from "lucide-react";
import { useState } from "react";
import { useInView } from "react-intersection-observer";
import Button from "../Button";

interface VideoCardProps {
    video: {
        title: string;
        url: string;
        duration: string;
        views: string;
        timeAgo: string;
        id: number;
    };
    onSuccess: () => void;
    onDelete: (id: number) => void;
}

export default function VideoCard(props: VideoCardProps) {
    const { video, onDelete } = props;
    const { id, title, url, duration = "30 mins", views = "100 views", timeAgo = "1 ngày trước" } = video;

    const [showPlayer, setShowPlayer] = useState(false);
    const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.2 });

    // ---------- Convert URL thành embed ----------
    const getEmbedUrl = (url: string) => {
        // watch?v=
        const watchMatch = url.match(/v=([^&]+)/);
        if (watchMatch) {
            return `https://www.youtube.com/embed/${watchMatch[1]}`;
        }

        // youtu.be/
        const shortMatch = url.match(/youtu\.be\/([^?]+)/);
        if (shortMatch) {
            return `https://www.youtube.com/embed/${shortMatch[1]}`;
        }

        // youtube.com/embed
        const embedMatch = url.match(/embed\/([^?]+)/);
        if (embedMatch) {
            return `https://www.youtube.com/embed/${embedMatch[1]}`;
        }

        // youtube shorts
        const shortsMatch = url.match(/shorts\/([^?]+)/);
        if (shortsMatch) {
            return `https://www.youtube.com/embed/${shortsMatch[1]}`;
        }

        // Google Drive
        const driveMatch = url.match(/drive\.google\.com\/file\/d\/([^/]+)/);
        if (driveMatch) {
            return `https://drive.google.com/file/d/${driveMatch[1]}/preview`;
        }

        return url;
    };

    const embedUrl = getEmbedUrl(url);

    // ---------- Thumbnail ----------
    const getThumbnail = () => {
        const ytMatch = embedUrl.match(/embed\/([^?]+)/);
        if (ytMatch) {
            return `https://img.youtube.com/vi/${ytMatch[1]}/hqdefault.jpg`;
        }

        // Google Drive → không có thumbnail
        return "/default-video-thumbnail.jpg";
    };

    const thumbnail = getThumbnail();

    return (
        <>
            <motion.div
                ref={ref}
                className="w-full bg-white rounded-2xl shadow-md overflow-hidden transition-transform duration-300 hover:-translate-y-2 hover:shadow-lg"
            >
                <div
                    className="relative bg-gray-800 h-40 flex items-center justify-center cursor-pointer"
                    onClick={() => setShowPlayer(true)}
                >
                    {inView ? (
                        <img
                            src={thumbnail}
                            // alt={title}
                            className="absolute inset-0 w-full h-full object-cover opacity-80"
                            loading="lazy"
                            alt="thumbnail"
                        />
                    ) : (
                        <div className="absolute inset-0 w-full h-full bg-gray-300 animate-pulse" />
                    )}

                    {/* Play Button */}
                    <button className="relative z-10 bg-white/80 rounded-full p-3">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="w-5 h-5 text-gray-800"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                        >
                            <path d="M8 5v14l11-7z" />
                        </svg>
                    </button>

                    <span className="absolute bottom-2 right-2 text-xs bg-black/70 text-white px-2 py-1 rounded-md">
                        {duration}
                    </span>

                    {/* Delete button */}
                    <span
                        onClick={(e) => {
                            e.stopPropagation();
                            onDelete(id);
                        }}
                        className="opacity-80 hover:opacity-100 hover:scale-105 absolute top-1 right-1 text-xs bg-black/70 text-white px-2 py-1 rounded-md"
                    >
                        <i className="fa fa-trash"></i>
                    </span>
                </div>

                {/* Info */}
                <div className="p-4 space-y-2">
                    <h3 className="font-bold line-clamp-2 text-left text-[18px]">
                        {title}
                    </h3>

                    <div className="flex items-center justify-between text-[#6c757d] text-sm">
                        <span className="flex items-center gap-1">
                            <Eye className="w-4 h-4" />
                            {views}
                        </span>
                        <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {timeAgo}
                        </span>
                    </div>

                    <Button
                        onClick={() => setShowPlayer(true)}
                        label="Xem bài giảng"
                        styleButton="w-full bg-secondary"
                    />
                </div>
            </motion.div>

            {/* Popup Video Player */}
            {showPlayer && (
                <div
                    className="fixed inset-0 bg-black/60 flex items-center justify-center z-50"
                    onClick={() => setShowPlayer(false)}
                >
                    <div
                        className="bg-black rounded-xl overflow-hidden shadow-lg w-11/12 md:w-2/3 lg:w-1/2 aspect-video"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <iframe
                            className="w-full h-full"
                            src={embedUrl + "?autoplay=1"}
                            title={title}
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        ></iframe>
                    </div>
                </div>
            )}
        </>
    );
}
